/**
 * PAL main CTA behavior (sections/pal-main-cta.liquid): media carousel with
 * pagination dots and drag-free thumbnails, plus variant radio sync.
 *
 * Boots once per `[data-rtnu-pal-main-cta]` section. The section passes its
 * ids through data attributes:
 *   data-section-id      Shopify section id (carousel/thumbnail node ids)
 *   data-form-id         product form id
 *   data-currency-symbol currency symbol for per-stick pricing
 *   data-b2b             present when the customer is B2B
 *
 * The buy box itself lives in assets/rtnu-buybox.js.
 */
(() => {
  if (window.__rtnuPalMainCtaBound) return;
  window.__rtnuPalMainCtaBound = true;

  const MAIN_OPTIONS = { align: 'start', containScroll: 'trimSnaps', slidesToScroll: 1 };
  const THUMB_OPTIONS = { align: 'start', containScroll: 'trimSnaps', dragFree: true };
  const isDesktop = () => window.innerWidth > 768;

  const initCarousel = (section, sectionId) => {
    const mainNode = section.querySelector(`#pdp-main-carousel2-${sectionId}`);
    const thumbsNode = section.querySelector(`#pdp-main-thumbnails2-${sectionId}`);
    const viewport = mainNode?.querySelector('.embla__viewport');
    if (!mainNode || !viewport || typeof EmblaCarousel !== 'function') return null;

    if (typeof mainNode._emblaCleanup === 'function') mainNode._emblaCleanup();

    const dots = mainNode.querySelector('.embla__dots');
    let main = EmblaCarousel(viewport, MAIN_OPTIONS);
    mainNode._emblaApi = main;

    let thumbs = null;
    const thumbClickHandlers = [];

    const destroyThumbs = () => {
      thumbClickHandlers.forEach(({ slide, handler }) => slide.removeEventListener('click', handler));
      thumbClickHandlers.length = 0;
      thumbs?.destroy();
      thumbs = null;
      thumbsNode?.querySelectorAll('.embla__slide').forEach((slide) => slide.classList.remove('is-selected'));
    };

    const rebuildDots = () => {
      if (!dots || !main) return;
      dots.replaceChildren(
        ...main.scrollSnapList().map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'embla__dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => main.scrollTo(index));
          return dot;
        })
      );
    };

    const updateDots = () => {
      if (!dots || !main) return;
      const selected = main.selectedScrollSnap();
      dots.querySelectorAll('.embla__dot').forEach((dot, index) => dot.classList.toggle('is-active', index === selected));
    };

    const updateThumbs = () => {
      if (!main || !thumbsNode) return;
      const selected = main.selectedScrollSnap();
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => {
        slide.classList.toggle('is-selected', index === selected);
      });
      thumbs?.scrollTo(selected);
    };

    const initThumbs = () => {
      if (!main || !thumbsNode || !isDesktop()) {
        destroyThumbs();
        return;
      }
      if (thumbs) return;
      const thumbsViewport = thumbsNode.querySelector('.embla__viewport');
      if (!thumbsViewport) return;

      thumbs = EmblaCarousel(thumbsViewport, THUMB_OPTIONS);
      thumbsNode.querySelectorAll('.embla__slide').forEach((slide, index) => {
        const handler = () => main?.scrollTo(index);
        slide.addEventListener('click', handler);
        thumbClickHandlers.push({ slide, handler });
      });
    };

    main.on('select', () => {
      updateDots();
      updateThumbs();
    });
    main.on('reInit', () => {
      rebuildDots();
      updateDots();
      updateThumbs();
    });

    rebuildDots();
    initThumbs();
    updateDots();
    updateThumbs();

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (!main) return;
        main.reInit();
        initThumbs();
        updateDots();
        updateThumbs();
      }, 150);
    };
    window.addEventListener('resize', onResize);

    mainNode._emblaCleanup = () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      destroyThumbs();
      main?.destroy();
      main = null;
      delete mainNode._emblaApi;
    };

    return main;
  };

  const initVariants = (section, form, sectionId, currencySymbol, isB2b) => {
    const productId = form.querySelector('[name="id"]');
    const slides = section.querySelectorAll(`#pdp-main-carousel2-${sectionId} .embla__slide`);
    const submitPrice = form.querySelector('.rtnu-product__form-submit-price');

    form.querySelectorAll('.rtnu-product__variants .rtnu-option').forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      const singlePrice = option.querySelector('.rtnu-product__form-item-single-cost-price');
      if (!radio) return;

      // Per-stick price for pouch products.
      const { priceRaw, itemCount } = radio.dataset;
      if (singlePrice && itemCount) {
        singlePrice.textContent = `${currencySymbol}${(priceRaw / itemCount).toFixed(2)}`;
      }

      option.addEventListener('change', (event) => {
        const { value: variantId, dataset } = event.target;

        if (isB2b && submitPrice) submitPrice.innerHTML = `${dataset.price} &bull; Add to Cart`;
        if (productId) productId.value = variantId;

        slides.forEach((slide, index) => {
          if (slide.dataset.variant !== undefined && slide.dataset.variant == variantId) {
            section.querySelector(`#pdp-main-carousel2-${sectionId}`)?._emblaApi?.scrollTo(index);
          }
        });
      });
    });
  };

  const initSection = (section) => {
    if (section.dataset.rtnuPalMainCtaReady) return;
    section.dataset.rtnuPalMainCtaReady = 'true';

    const { sectionId, formId, currencySymbol = '$' } = section.dataset;
    const form = section.querySelector(`#${CSS.escape(formId)}`);

    initCarousel(section, sectionId);
    if (form) initVariants(section, form, sectionId, currencySymbol, section.hasAttribute('data-b2b'));
  };

  const initAll = () => document.querySelectorAll('[data-rtnu-pal-main-cta]').forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
